import React, { useEffect, useRef } from "react";
import { LexicalComposer } from "@lexical/react/LexicalComposer";
import { RichTextPlugin } from "@lexical/react/LexicalRichTextPlugin";
import { ContentEditable } from "@lexical/react/LexicalContentEditable";
import { HistoryPlugin } from "@lexical/react/LexicalHistoryPlugin";
import { ListPlugin } from "@lexical/react/LexicalListPlugin";
import { LinkPlugin } from "@lexical/react/LexicalLinkPlugin";
import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import {
  $getSelection,
  $isRangeSelection,
  $getRoot,
  $getNodeByKey,
  $createParagraphNode,
  $createTextNode,
  $createLineBreakNode,
  $isRootOrShadowRoot,
  $isTextNode,
} from "lexical";
import { $getNearestNodeOfType, $findMatchingParent } from "@lexical/utils";
import {
  $createHeadingNode,
  $createQuoteNode,
  HeadingNode,
  QuoteNode,
} from "@lexical/rich-text";
import {
  ListNode,
  ListItemNode,
  $createListNode,
  $createListItemNode,
  $isListNode,
  $isListItemNode,
} from "@lexical/list";
import { $createLinkNode, LinkNode } from "@lexical/link";
import {
  $patchStyleText,
  $getSelectionStyleValueForProperty,
  $setBlocksType,
} from "@lexical/selection";
import { OnChangePlugin } from "@lexical/react/LexicalOnChangePlugin";
import { AutoFocusPlugin } from "@lexical/react/LexicalAutoFocusPlugin";
import {
  INSERT_ORDERED_LIST_COMMAND,
  INSERT_UNORDERED_LIST_COMMAND,
  REMOVE_LIST_COMMAND,
} from "@lexical/list";
import { TOGGLE_LINK_COMMAND } from "@lexical/link";
import { $generateHtmlFromNodes, $generateNodesFromDOM } from "@lexical/html";
import { MarkNode, $createMarkNode } from "@lexical/mark";
import generateHtmlWithStylesFromNodes from "../canvas-core/generateHtmlWithStylesFromNodes";
import ColorPlugin from "../canvas-core/ColorPlugin";
import {
  COMMAND_PRIORITY_HIGH,
  INDENT_CONTENT_COMMAND,
  KEY_TAB_COMMAND,
  PASTE_COMMAND,
} from "lexical";
import { buildImportMap } from "../../../../util/lexical";
import { mergeRegister } from "@lexical/utils";
import { isLightColor } from "../text/utils/colorUtils";

const flattenNestedLists = (html) => {
  if (
    !html ||
    typeof window === "undefined" ||
    typeof window.DOMParser === "undefined"
  ) {
    return html;
  }

  try {
    const parser = new window.DOMParser();
    const doc = parser.parseFromString(`<div>${html}</div>`, "text/html");
    const container = doc.body.firstElementChild;

    if (!container) {
      return html;
    }

    const isListElement = (node) =>
      node && (node.tagName === "UL" || node.tagName === "OL");

    const hasDisplayableContent = (li) =>
      Array.from(li.childNodes).some((node) => {
        if (node.nodeType === Node.TEXT_NODE) {
          return node.textContent.trim().length > 0;
        }
        if (node.nodeType === Node.ELEMENT_NODE) {
          const tagName = node.tagName;
          if (tagName === "UL" || tagName === "OL") {
            return false;
          }
          return true;
        }
        return false;
      });

    const flattenList = (list) => {
      if (!isListElement(list)) return;

      let mutated = false;

      Array.from(list.children)
        .filter((child) => child.tagName === "LI")
        .forEach((li) => {
          const childLists = Array.from(li.children).filter((child) =>
            isListElement(child)
          );

          childLists.forEach(flattenList);

          if (childLists.length === 1 && !hasDisplayableContent(li)) {
            const nestedList = childLists[0];
            const nestedItems = Array.from(nestedList.children).filter(
              (child) => child.tagName === "LI"
            );

            nestedItems.forEach((nestedItem) => {
              list.insertBefore(nestedItem, li);
            });

            li.remove();
            mutated = true;
          }
        });

      if (mutated) {
        flattenList(list);
      }
    };

    Array.from(container.querySelectorAll("ul, ol")).forEach(flattenList);

    return container.innerHTML;
  } catch (error) {
    console.warn("flattenNestedLists failed", error);
    return html;
  }
};

// ============================================================================
// SHARED HTML PARSING UTILITIES
// ============================================================================
// These utilities are shared between ContentInitializationPlugin and PastePlugin
// to ensure consistent HTML-to-Lexical conversion behavior

// Helper function to extract all styles from element and its ancestors
function extractStyles(element) {
  const styles = {};
  let current = element;

  // Walk up the DOM tree to collect inherited styles
  while (current && current.nodeType === Node.ELEMENT_NODE) {
    const styleAttr = current.getAttribute("style");
    if (styleAttr) {
      styleAttr.split(";").forEach((pair) => {
        const [key, value] = pair.split(":").map((x) => x && x.trim());
        if (key && value && !styles[key]) {
          // Don't override more specific styles
          styles[key] = value;
        }
      });
    }

    // Handle semantic tags
    switch (current.tagName) {
      case "B":
      case "STRONG":
        if (!styles["font-weight"]) styles["font-weight"] = "bold";
        break;
      case "I":
      case "EM":
        if (!styles["font-style"]) styles["font-style"] = "italic";
        break;
      case "U":
        if (!styles["text-decoration"]) styles["text-decoration"] = "underline";
        break;
      case "S":
      case "STRIKE":
      case "DEL":
        if (!styles["text-decoration"])
          styles["text-decoration"] = "line-through";
        break;
    }

    current = current.parentNode;
  }

  return styles;
}

// Helper function to convert styles object to CSS string
function stylesToString(styles) {
  return Object.entries(styles)
    .map(([key, value]) => `${key}: ${value}`)
    .join("; ");
}

// Helper function to apply styles to any lexical node
function applyStylesToNode(lexicalNode, styles) {
  // Create a copy of styles to avoid mutating the original
  const stylesToApply = { ...styles };

  // For text nodes, apply format flags and remove those styles from the style object
  // to avoid double-application
  if (lexicalNode.getType() === "text") {
    // Get existing format flags to preserve them
    let formatFlags = lexicalNode.getFormat();

    // Lexical TextFormatType constants:
    // BOLD = 1, ITALIC = 2, STRIKETHROUGH = 4, UNDERLINE = 8

    // Handle text-decoration
    if (stylesToApply["text-decoration"]) {
      const textDecoration = stylesToApply["text-decoration"];
      if (textDecoration.includes("underline")) {
        formatFlags |= 8; // TextFormatType.UNDERLINE
      } else {
        // Remove underline if not specified
        formatFlags &= ~8;
      }
      if (textDecoration.includes("line-through")) {
        formatFlags |= 4; // TextFormatType.STRIKETHROUGH
      } else {
        // Remove strikethrough if not specified
        formatFlags &= ~4;
      }
      // Remove text-decoration from styles since we're applying it via format
      delete stylesToApply["text-decoration"];
    }

    // Handle font-weight
    if (stylesToApply["font-weight"]) {
      const fontWeight = stylesToApply["font-weight"];
      if (fontWeight === "bold" || parseInt(fontWeight) >= 600) {
        formatFlags |= 1; // TextFormatType.BOLD
      } else {
        // Remove bold if not specified
        formatFlags &= ~1;
      }
      // Remove font-weight from styles since we're applying it via format
      delete stylesToApply["font-weight"];
    }

    // Handle font-style
    if (stylesToApply["font-style"]) {
      if (stylesToApply["font-style"] === "italic") {
        formatFlags |= 2; // TextFormatType.ITALIC
      } else {
        // Remove italic if not specified
        formatFlags &= ~2;
      }
      // Remove font-style from styles since we're applying it via format
      delete stylesToApply["font-style"];
    }

    // Apply format flags (this will preserve existing formatting and add new)
    lexicalNode.setFormat(formatFlags);
  }

  // Only apply remaining styles (color, background-color, etc.) via style property
  if (Object.keys(stylesToApply).length > 0) {
    const styleString = stylesToString(stylesToApply);
    lexicalNode.__style = styleString;

    // Store styles for DOM application - create a closure to capture stylesToApply
    const capturedStyles = { ...stylesToApply };

    // Only override createDOM if we have styles to apply
    const originalCreateDOM = lexicalNode.createDOM;
    if (originalCreateDOM) {
      lexicalNode.createDOM = function (config) {
        const dom = originalCreateDOM.call(this, config);

        // Apply captured styles to the DOM element
        Object.entries(capturedStyles).forEach(([key, value]) => {
          try {
            if (key === "color") {
              dom.style.color = value;
            } else if (
              key === "background-color" ||
              key === "backgroundColor"
            ) {
              dom.style.backgroundColor = value;
            } else if (key.includes("-")) {
              dom.style.setProperty(key, value);
            } else {
              dom.style[key] = value;
            }
          } catch (e) {
            // Silently ignore style application errors
          }
        });

        return dom;
      };
    }

    // Override updateDOM with captured styles
    const originalUpdateDOM = lexicalNode.updateDOM;
    if (originalUpdateDOM) {
      lexicalNode.updateDOM = function (prevNode, dom, config) {
        const result = originalUpdateDOM.call(this, prevNode, dom, config);

        // Reapply captured styles
        Object.entries(capturedStyles).forEach(([key, value]) => {
          try {
            if (key === "color") {
              dom.style.color = value;
            } else if (
              key === "background-color" ||
              key === "backgroundColor"
            ) {
              dom.style.backgroundColor = value;
            } else if (key.includes("-")) {
              dom.style.setProperty(key, value);
            } else {
              dom.style[key] = value;
            }
          } catch (e) {
            // Silently ignore style application errors
          }
        });

        return result;
      };
    }
  }
}

// Helper function to convert DOM nodes to Lexical nodes with style preservation
function domToLexical(node, inheritedStyles = {}) {
  if (node.nodeType === Node.TEXT_NODE) {
    const textContent = node.textContent;
    if (!textContent.trim()) return null; // Skip empty text nodes

    const textNode = $createTextNode(textContent);

    // Apply inherited styles to text node
    if (Object.keys(inheritedStyles).length > 0) {
      applyStylesToNode(textNode, inheritedStyles);
    }

    return textNode;
  }

  if (node.nodeType === Node.ELEMENT_NODE) {
    // Extract styles from current element and merge with inherited
    const currentStyles = extractStyles(node);
    const mergedStyles = { ...inheritedStyles, ...currentStyles };

    // Process children with merged styles
    const children = Array.from(node.childNodes)
      .map((child) => domToLexical(child, mergedStyles))
      .filter(Boolean)
      .flat();

    let lexicalNode;

    switch (node.tagName) {
      case "P":
      case "DIV":
        lexicalNode = $createParagraphNode();
        break;
      case "UL":
        lexicalNode = $createListNode("bullet");
        break;
      case "OL":
        lexicalNode = $createListNode("number");
        break;
      case "LI":
        lexicalNode = $createListItemNode();
        break;
      case "BLOCKQUOTE":
        lexicalNode = new QuoteNode();
        break;
      case "A":
        lexicalNode = new LinkNode(node.getAttribute("href") || "", {
          target: "_blank",
        });
        break;
      case "BR":
        return $createLineBreakNode();
      case "H1":
      case "H2":
      case "H3":
      case "H4":
      case "H5":
      case "H6":
        lexicalNode = $createHeadingNode(node.tagName.toLowerCase());
        break;
      case "B":
      case "STRONG":
      case "I":
      case "EM":
      case "U":
      case "S":
      case "STRIKE":
      case "DEL":
      case "SPAN":
        // These are inline formatting elements, don't create a container
        // Just return the styled children
        return children;
      default:
        // For unknown elements, just return the children
        return children;
    }

    if (lexicalNode) {
      // Apply styles to the lexical node itself using setStyle
      if (Object.keys(currentStyles).length > 0) {
        applyStylesToNode(lexicalNode, currentStyles);
      }

      // Append children to the lexical node
      children.forEach((child) => {
        if (Array.isArray(child)) {
          child.forEach((n) => lexicalNode.append(n));
        } else if (child) {
          lexicalNode.append(child);
        }
      });
      return lexicalNode;
    } else {
      return children;
    }
  }

  return null;
}

// ============================================================================
// CONTENT INITIALIZATION PLUGIN
// ============================================================================

function ContentInitializationPlugin({ content, id, color, underline, highlight }) {
  const [editor] = useLexicalComposerContext();
  const initializedRef = useRef(false);
  const initialContentRef = useRef(content); // Store the initial content

  useEffect(() => {
    if (content && content.trim() && !initializedRef.current) {
      setTimeout(() => {
        editor.update(() => {
          const root = $getRoot();

          // Check if editor is truly empty before initializing
          if (root.getTextContent().trim() === "") {
            root.clear();
            const isHtml = /<\/?[a-z][\s\S]*?>/i.test(content);
            const normalizedContent = isHtml
              ? flattenNestedLists(content)
              : content;

            if (isHtml) {
              // Use Lexical's built-in HTML import which respects buildImportMap() patches
              // This ensures lists and text are properly preserved (same as RichTextForm)
              const parser = new DOMParser();
              const dom = parser.parseFromString(
                normalizedContent,
                "text/html"
              );

              // Use $generateNodesFromDOM which respects the html.import config (buildImportMap)
              // This is the same method RichTextForm uses and ensures proper list handling
              const nodes = $generateNodesFromDOM(editor, dom, {
                preserveWhitespace: true,
              });

              // Append to root
              if (nodes.length > 0) {
                root.append(...nodes);
              }

              // If no content was added, add an empty paragraph
              if (root.isEmpty()) {
                const paragraph = $createParagraphNode();
                root.append(paragraph);
              }
            } else {
              // For plain text, create a simple paragraph with text
              const paragraph = $createParagraphNode();
              const textNode = $createTextNode(normalizedContent);
              paragraph.append(textNode);
              root.append(paragraph);
            }

            // Set initialized flag
            initializedRef.current = true;
          }
        });
      }, 10);
    }
  }, []); // Empty dependency array - only run once on mount

  // Separate effect to handle content changes after initialization
  useEffect(() => {
    // Only update if content has changed from the initial content
    // and we've already initialized
    if (initializedRef.current && content !== initialContentRef.current) {
      // Check if editor is currently focused (user is typing)
      const editorElement = editor.getRootElement();
      const isFocused = editorElement && document.activeElement === editorElement;
      
      // Don't update if user is actively editing (to preserve cursor position)
      if (isFocused) {
        // Still update the reference so we don't trigger this again
        initialContentRef.current = content;
        return;
      }

      // Update the reference to the new content
      initialContentRef.current = content;

      editor.getEditorState().read(() => {
        const root = $getRoot();
        const currentText = root.getTextContent().trim();
        
        // If content prop is empty/cleared, clear the editor
        if ((!content || content.trim() === "") && currentText !== "") {
          editor.update(() => {
            root.clear();
            // Add empty paragraph to maintain editor structure
            const paragraph = $createParagraphNode();
            root.append(paragraph);
          });
          return;
        }
        
        // Only reinitialize if the editor is empty and we have new content
        if (currentText === "" && content && content.trim()) {
          initializedRef.current = false; // Allow reinitialization
        }
      });
    }
  }, [content, editor]);

  return null;
}

// ============================================================================
// PASTE PLUGIN
// ============================================================================
// This plugin intercepts paste events and uses the same HTML parsing logic
// as ContentInitializationPlugin to ensure formatting is properly preserved

function PastePlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return editor.registerCommand(
      PASTE_COMMAND,
      (event) => {
        const clipboardData = event.clipboardData;
        if (!clipboardData) return false;

        // Try to get HTML content first (richest format)
        const htmlData = clipboardData.getData("text/html");

        if (htmlData && htmlData.trim()) {
          event.preventDefault();

          editor.update(() => {
            const selection = $getSelection();
            if (!$isRangeSelection(selection)) return;

            const normalizedHtmlData = flattenNestedLists(htmlData);

            // Use Lexical's built-in HTML import which respects buildImportMap() patches
            // This ensures lists and text are properly preserved
            const parser = new DOMParser();
            const dom = parser.parseFromString(normalizedHtmlData, "text/html");

            // Use $generateNodesFromDOM which respects the html.import config (buildImportMap)
            // This is the same method RichTextForm uses and ensures proper list handling
            const nodes = $generateNodesFromDOM(editor, dom, {
              preserveWhitespace: true,
            });

            // Insert the nodes at the current selection
            if (nodes.length > 0) {
              selection.insertNodes(nodes);
            }
          });

          return true; // Event handled
        }

        // Fallback to plain text if no HTML
        const textData = clipboardData.getData("text/plain");
        if (textData && textData.trim()) {
          // Let Lexical handle plain text paste normally
          return false;
        }

        return false;
      },
      COMMAND_PRIORITY_HIGH
    );
  }, [editor]);

  return null;
}

function SingleLevelListPlugin() {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    return mergeRegister(
      editor.registerCommand(
        INDENT_CONTENT_COMMAND,
        () => true,
        COMMAND_PRIORITY_HIGH
      ),
      editor.registerCommand(
        KEY_TAB_COMMAND,
        (event) => {
          if (event) {
            event.preventDefault();
          }
          return true;
        },
        COMMAND_PRIORITY_HIGH
      )
    );
  }, [editor]);

  return null;
}

// Helper function to find the list item node that contains the selection
function findListItemInSelection(selection) {
  if (!$isRangeSelection(selection)) return null;

  // First, try using getNodes() to get all nodes in the selection
  // This is more reliable for finding the list item
  const nodes = selection.getNodes();

  for (const node of nodes) {
    // Check if the node itself is a list item using Lexical's type check
    if ($isListItemNode(node)) {
      return node;
    }

    // Walk up from this node to find list item
    let parent = node.getParent();
    while (parent) {
      if ($isListItemNode(parent)) {
        return parent;
      }
      parent = parent.getParent();
    }
  }

  // Fallback: Check anchor node
  const anchorNode = selection.anchor.getNode();

  // If anchor node is already a list item, return it
  if ($isListItemNode(anchorNode)) {
    return anchorNode;
  }

  let parent = anchorNode.getParent();

  // Walk up from anchor node to find list item
  while (parent) {
    if ($isListItemNode(parent)) {
      return parent;
    }
    parent = parent.getParent();
  }

  // If not found via anchor, try focus node as fallback
  const focusNode = selection.focus.getNode();
  if (focusNode !== anchorNode) {
    // If focus node is already a list item, return it
    if ($isListItemNode(focusNode)) {
      return focusNode;
    }

    parent = focusNode.getParent();

    // Walk up from focus node to find list item
    while (parent) {
      if ($isListItemNode(parent)) {
        return parent;
      }
      parent = parent.getParent();
    }
  }

  return null;
}

// Helper function to find the list node that contains the selection
function findListNodeInSelection(selection) {
  if (!$isRangeSelection(selection)) return null;

  // First, try using getNodes() to get all nodes in the selection
  const nodes = selection.getNodes();

  for (const node of nodes) {
    // Check if the node itself is a list
    if ($isListNode(node)) {
      return node;
    }

    // Walk up from this node to find list
    let parent = node.getParent();
    while (parent) {
      if ($isListNode(parent)) {
        return parent;
      }
      parent = parent.getParent();
    }
  }

  // Fallback: Check anchor node
  const anchorNode = selection.anchor.getNode();
  let parent = anchorNode.getParent();

  while (parent) {
    if ($isListNode(parent)) {
      return parent;
    }
    parent = parent.getParent();
  }

  // Fallback: Check focus node
  const focusNode = selection.focus.getNode();
  if (focusNode !== anchorNode) {
    parent = focusNode.getParent();
    while (parent) {
      if ($isListNode(parent)) {
        return parent;
      }
      parent = parent.getParent();
    }
  }

  return null;
}

// Helper function to check if selection is inside a list of specific type
function isSelectionInList(selection, listType) {
  if (!selection) return false;
  const listNode = findListNodeInSelection(selection);
  if (!listNode) return false;
  const nodeListType = listNode.getListType();
  return nodeListType === listType;
}

// Helper function to convert an entire list from one type to another
function convertListType(parentList, newListType) {
  if (!parentList || parentList.getListType() === newListType) {
    return parentList;
  }

  // Create a new list with the target type
  const newList = $createListNode(newListType);

  // Get all list items from the original list (create a copy of the array since
  // getChildren() returns a live array that will change as we move items)
  const allItems = Array.from(parentList.getChildren());

  // Move all items to the new list (this automatically removes them from parentList)
  allItems.forEach((item) => {
    newList.append(item);
  });

  // Insert the new list before the old list
  parentList.insertBefore(newList);

  // Remove the now-empty old list
  parentList.remove();

  return newList;
}

// Helper function to convert a list item to a paragraph
function convertListItemToParagraph(listItem) {
  const paragraph = $createParagraphNode();

  // Move all children from list item to paragraph
  const children = listItem.getChildren();
  children.forEach((child) => {
    paragraph.append(child);
  });

  // Get the parent list
  const parentList = listItem.getParent();

  // Check if this is the first item in the list
  const isFirstItem = parentList.getFirstChild() === listItem;

  // Check if this is the last item in the list
  const isLastItem = parentList.getLastChild() === listItem;

  // Get the index of the current item
  const allItems = parentList.getChildren();
  const itemIndex = allItems.indexOf(listItem);

  if (isFirstItem) {
    // First item: insert paragraph before the list to maintain top position
    // The key is to insert BEFORE removing the item, and ensure it's at the container level
    const container = parentList.getParent();

    // IMPORTANT: Insert paragraph before the list BEFORE removing the item
    // This ensures the paragraph is positioned correctly relative to the list
    parentList.insertBefore(paragraph);

    // Verify the paragraph is actually before the list
    // If not, it means insertBefore didn't work as expected
    const paragraphParent = paragraph.getParent();
    const listParent = parentList.getParent();

    // If paragraph and list are siblings, check their order
    if (paragraphParent === listParent) {
      const containerChildren = Array.from(container.getChildren());
      const paragraphIndex = containerChildren.indexOf(paragraph);
      const listIndex = containerChildren.indexOf(parentList);

      // If paragraph is after the list, we need to fix it
      if (paragraphIndex > listIndex || paragraphIndex === -1) {
        // Remove paragraph and re-insert it before the list
        paragraph.remove();
        parentList.insertBefore(paragraph);
      }
    }

    // Now remove the list item
    listItem.remove();

    // After removing the item, verify the paragraph is still before the list
    // This is important because removing the item might affect node ordering
    if (parentList && parentList.isAttached() && paragraph.isAttached()) {
      const container = parentList.getParent();
      if (container === paragraph.getParent()) {
        const containerChildren = Array.from(container.getChildren());
        const paragraphIndex = containerChildren.indexOf(paragraph);
        const listIndex = containerChildren.indexOf(parentList);

        // If paragraph is after the list, fix it
        if (paragraphIndex > listIndex) {
          paragraph.remove();
          parentList.insertBefore(paragraph);
        }
      }
    }

    // If the list is now empty, remove it
    if (parentList.getChildren().length === 0) {
      parentList.remove();
    }
  } else if (isLastItem) {
    // Last item: insert paragraph after the list
    parentList.insertAfter(paragraph);

    // Remove the list item
    listItem.remove();

    // If the list is now empty, remove it
    if (parentList.getChildren().length === 0) {
      parentList.remove();
    }
  } else {
    // Middle item: split the list and insert paragraph between the two parts
    // CRITICAL: We must maintain the order: first list → paragraph → second list
    const listType = parentList.getListType();
    const container = parentList.getParent();

    // Get items after the current item
    const itemsAfter = allItems.slice(itemIndex + 1);

    // Create a new list for items after the current item
    const secondList = $createListNode(listType);

    // First, move items after the current one to the second list
    // This must happen before removing the item to maintain references
    itemsAfter.forEach((item) => {
      secondList.append(item);
    });

    // Remove the current list item
    listItem.remove();

    // Now insert paragraph and second list in the correct order
    // The order MUST be: parentList → paragraph → secondList
    // Use a more reliable method: find parentList's position and insert after it
    const containerChildrenBeforeInsert = Array.from(container.getChildren());
    const parentListPosition =
      containerChildrenBeforeInsert.indexOf(parentList);

    if (parentListPosition >= 0) {
      if (itemsAfter.length > 0) {
        // Find the next sibling after parentList to insert before it
        const nextSibling = parentList.getNextSibling();

        if (nextSibling) {
          // Insert paragraph before the next sibling (which is after parentList)
          nextSibling.insertBefore(paragraph);
        } else {
          // No next sibling, insert after parentList
          parentList.insertAfter(paragraph);
        }

        // Now insert second list after paragraph
        const paragraphNextSibling = paragraph.getNextSibling();
        if (paragraphNextSibling) {
          paragraphNextSibling.insertBefore(secondList);
        } else {
          paragraph.insertAfter(secondList);
        }

        // Final verification: ensure correct order
        const finalChildren = Array.from(container.getChildren());
        const finalParentListIdx = finalChildren.indexOf(parentList);
        const finalParagraphIdx = finalChildren.indexOf(paragraph);
        const finalSecondListIdx = finalChildren.indexOf(secondList);

        // If order is wrong, fix it by removing and re-inserting in correct order
        if (
          finalParentListIdx >= 0 &&
          finalParagraphIdx >= 0 &&
          finalSecondListIdx >= 0
        ) {
          const correctOrder =
            finalParentListIdx < finalParagraphIdx &&
            finalParagraphIdx < finalSecondListIdx;

          if (!correctOrder) {
            // Remove both and re-insert in correct order
            paragraph.remove();
            secondList.remove();

            // Re-insert in correct order
            parentList.insertAfter(paragraph);
            paragraph.insertAfter(secondList);
          }
        }
      } else {
        // No items after, just insert paragraph after the list
        const nextSibling = parentList.getNextSibling();
        if (nextSibling) {
          nextSibling.insertBefore(paragraph);
        } else {
          parentList.insertAfter(paragraph);
        }
      }
    }

    // If the first list is now empty, remove it
    if (parentList.getChildren().length === 0) {
      parentList.remove();
    }
  }

  return paragraph;
}

// Formatting Plugin for Lexical
function FormattingPlugin({
  onColorChange,
  onUnderlineChange,
  onHighlightChange,
  onNumberedList,
  onBulletedList,
  onLinkChange,
}) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    // Handle color change
    if (onColorChange) {
      const handleColorChange = (color) => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $patchStyleText(selection, { color });
          }
        });
        onColorChange(color);
      };

      // Expose the function globally for the toolbar to use
      window.handleLexicalColorChange = handleColorChange;
    }

    // Handle underline change
    if (onUnderlineChange) {
      const handleUnderlineChange = () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            // Use format flags instead of CSS styles for consistency
            const nodes = selection.getNodes();
            nodes.forEach((node) => {
              if (node.getType() === "text") {
                const currentFormat = node.getFormat();
                const hasUnderline = currentFormat & 8; // TextFormatType.UNDERLINE

                if (hasUnderline) {
                  // Remove underline
                  node.setFormat(currentFormat & ~8);
                } else {
                  // Add underline
                  node.setFormat(currentFormat | 8);
                }
              }
            });
          }
        });
        onUnderlineChange();
      };

      window.handleLexicalUnderlineChange = handleUnderlineChange;
    }

    // Handle highlight change
    if (onHighlightChange) {
      const handleHighlightChange = () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const currentBg = $getSelectionStyleValueForProperty(
              selection,
              "background-color",
              ""
            );
            const newBg =
              currentBg === "yellow" || currentBg === "rgb(255, 255, 0)"
                ? ""
                : "yellow";
            $patchStyleText(selection, { "background-color": newBg });
          }
        });
        onHighlightChange();
      };

      window.handleLexicalHighlightChange = handleHighlightChange;
    }

    // Handle numbered list - Using EXACT same approach as RichTextEditor formatNumberedList
    if (onNumberedList) {
      // Helper function to get block type (same as RichTextEditor ToolbarPlugin)
      const getBlockType = () => {
        let blockType = "paragraph";
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            // Use $findTopLevelElement helper (same as RichTextEditor)
            let element =
              anchorNode.getKey() === "root"
                ? anchorNode
                : $findMatchingParent(anchorNode, (e) => {
                    const parent = e.getParent();
                    return parent !== null && $isRootOrShadowRoot(parent);
                  });

            if (element === null) {
              element = anchorNode.getTopLevelElementOrThrow();
            }

            if ($isListNode(element)) {
              const parentList = $getNearestNodeOfType(anchorNode, ListNode);
              const listType = parentList
                ? parentList.getListType()
                : element.getListType();
              blockType = listType; // listType is already "number" or "bullet"
            } else {
              blockType = element.getType();
            }
          }
        });
        return blockType;
      };

      // formatParagraph helper (same as RichTextEditor)
      const formatParagraph = () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        });
      };

      const handleNumberedList = () => {
        const blockType = getBlockType();

        // Exact same logic as RichTextEditor formatNumberedList
        if (blockType !== "number") {
          editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined);
        } else {
          formatParagraph();
        }
        onNumberedList();
      };

      window.handleLexicalNumberedList = handleNumberedList;
    }

    // Handle bulleted list - Using EXACT same approach as RichTextEditor formatBulletList
    if (onBulletedList) {
      // Helper function to get block type (same as RichTextEditor ToolbarPlugin)
      const getBlockType = () => {
        let blockType = "paragraph";
        editor.getEditorState().read(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            const anchorNode = selection.anchor.getNode();
            // Use $findTopLevelElement helper (same as RichTextEditor)
            let element =
              anchorNode.getKey() === "root"
                ? anchorNode
                : $findMatchingParent(anchorNode, (e) => {
                    const parent = e.getParent();
                    return parent !== null && $isRootOrShadowRoot(parent);
                  });

            if (element === null) {
              element = anchorNode.getTopLevelElementOrThrow();
            }

            if ($isListNode(element)) {
              const parentList = $getNearestNodeOfType(anchorNode, ListNode);
              const listType = parentList
                ? parentList.getListType()
                : element.getListType();
              blockType = listType; // listType is already "number" or "bullet"
            } else {
              blockType = element.getType();
            }
          }
        });
        return blockType;
      };

      // formatParagraph helper (same as RichTextEditor)
      const formatParagraph = () => {
        editor.update(() => {
          const selection = $getSelection();
          if ($isRangeSelection(selection)) {
            $setBlocksType(selection, () => $createParagraphNode());
          }
        });
      };

      const handleBulletedList = () => {
        const blockType = getBlockType();

        // Exact same logic as RichTextEditor formatBulletList
        if (blockType !== "bullet") {
          editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined);
        } else {
          formatParagraph();
        }
        onBulletedList();
      };

      window.handleLexicalBulletedList = handleBulletedList;
    }

    // Handle link change
    if (onLinkChange) {
      const handleLinkChange = () => {
        const url = prompt("Enter URL", "https://");
        if (url) {
          editor.dispatchCommand(TOGGLE_LINK_COMMAND, url);
          onLinkChange();
        }
      };

      window.handleLexicalLinkChange = handleLinkChange;
    }

    return () => {
      // Cleanup global functions
      delete window.handleLexicalColorChange;
      delete window.handleLexicalUnderlineChange;
      delete window.handleLexicalHighlightChange;
      delete window.handleLexicalNumberedList;
      delete window.handleLexicalBulletedList;
      delete window.handleLexicalLinkChange;
    };
  }, [
    editor,
    onColorChange,
    onUnderlineChange,
    onHighlightChange,
    onNumberedList,
    onBulletedList,
    onLinkChange,
  ]);

  return null;
}

// Error boundary component
function LexicalErrorBoundary({ children }) {
  return (
    <div
      style={{
        border: "1px solid #ff6b6b",
        borderRadius: "4px",
        padding: "10px",
        backgroundColor: "#ffe6e6",
        color: "#cc0000",
      }}
    >
      {children}
    </div>
  );
}

// Plugin to export HTML on change with styling preservation
function HTMLExportOnChangePlugin({ onContentChange }) {
  const [editor] = useLexicalComposerContext();

  const handleChange = (editorState) => {
    editorState.read(() => {
      const root = $getRoot();
      const textContent = root.getTextContent();
      // Generate HTML with styling preserved

      const htmlContent = generateHtmlWithStylesFromNodes(editor);

      const normalizedHtmlContent = flattenNestedLists(htmlContent);

      onContentChange(normalizedHtmlContent, textContent);
    });
  };

  return <OnChangePlugin onChange={handleChange} />;
}

// Plugin to handle blur events
function BlurPlugin({ onBlur }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    const handleBlur = () => {
      if (onBlur) {
        onBlur();
      }
    };

    // Listen for blur events on the editor
    const editorElement = editor.getRootElement();
    if (editorElement) {
      editorElement.addEventListener("blur", handleBlur);
    }

    return () => {
      if (editorElement) {
        editorElement.removeEventListener("blur", handleBlur);
      }
    };
  }, [editor, onBlur]);

  return null;
}

// Plugin to make light-colored text visible on white backgrounds
function LightTextVisibilityPlugin({ editorId }) {
  const [editor] = useLexicalComposerContext();

  useEffect(() => {
    let updateTimeout = null;
    const LIGHT_TEXT_SHADOW = 
      "1px 1px 2px rgba(0, 0, 0, 0.8), " +
      "-1px -1px 2px rgba(0, 0, 0, 0.8), " +
      "1px -1px 2px rgba(0, 0, 0, 0.8), " +
      "-1px 1px 2px rgba(0, 0, 0, 0.8), " +
      "0 0 3px rgba(0, 0, 0, 0.9)";

    const updateLightTextStyles = () => {
      const editorElement = editor.getRootElement();
      if (!editorElement) return;

      // Find all elements that might have text content
      // Focus on elements with inline styles or that contain text nodes
      const allElements = editorElement.querySelectorAll("*");
      const processedElements = new Set();
      
      allElements.forEach((element) => {
        // Skip if already processed
        if (processedElements.has(element)) return;
        
        // Check if element has text content or is a text node
        const hasText = element.textContent && element.textContent.trim().length > 0;
        if (!hasText && element.children.length === 0) return;
        
        const computedStyle = window.getComputedStyle(element);
        const color = computedStyle.color;
        
        // Check if color is light
        if (color && isLightColor(color)) {
          // Check if shadow is already applied (to avoid re-applying)
          const currentTextShadow = element.style.textShadow || computedStyle.textShadow;
          const hasShadow = currentTextShadow && 
            currentTextShadow !== "none" && 
            currentTextShadow.includes("rgba(0, 0, 0");
          
          if (!hasShadow) {
            // Apply a dark text shadow to make light text visible on white background
            element.style.textShadow = LIGHT_TEXT_SHADOW;
            processedElements.add(element);
          }
        } else {
          // If color is not light, remove shadow if it was previously applied
          const currentTextShadow = element.style.textShadow || computedStyle.textShadow;
          if (currentTextShadow && currentTextShadow.includes("rgba(0, 0, 0")) {
            // Only remove if it's our shadow (check for the specific pattern)
            if (currentTextShadow === LIGHT_TEXT_SHADOW || 
                currentTextShadow.includes("1px 1px 2px rgba(0, 0, 0, 0.8)")) {
              element.style.textShadow = "";
            }
          }
        }
      });
    };

    // Debounced update function
    const debouncedUpdate = () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      updateTimeout = setTimeout(updateLightTextStyles, 50);
    };

    // Update styles on editor updates
    const unregister = editor.registerUpdateListener(() => {
      debouncedUpdate();
    });

    // Initial update
    setTimeout(updateLightTextStyles, 100);

    // Also use MutationObserver for DOM changes
    const editorElement = editor.getRootElement();
    if (editorElement) {
      const observer = new MutationObserver(() => {
        debouncedUpdate();
      });

      observer.observe(editorElement, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ["style"],
      });

      return () => {
        if (updateTimeout) {
          clearTimeout(updateTimeout);
        }
        unregister();
        observer.disconnect();
      };
    }

    return () => {
      if (updateTimeout) {
        clearTimeout(updateTimeout);
      }
      unregister();
    };
  }, [editor, editorId]);

  return null;
}

// ============================================================================
// HIGHLIGHTING STATE PRESERVATION PLUGIN
// ============================================================================

function HighlightingStatePreservationPlugin({ color, underline, highlight }) {
  const [editor] = useLexicalComposerContext();
  const initializedRef = useRef(false);

  useEffect(() => {
    // Only run once after editor has been initialized with content
    if (!initializedRef.current) {
      setTimeout(() => {
        editor.update(() => {
          const root = $getRoot();
          
          // Only proceed if there's content in the editor
          if (!root.isEmpty()) {
            // Get the current HTML content to check if highlighting is already applied
            const currentHtml = $generateHtmlFromNodes(editor, { null: true });
            
            // Apply highlighting state only if not already present in HTML
            if (color && !currentHtml.includes('color:')) {
              // Color is not in HTML, apply it to all text nodes
              const applyColor = (node) => {
                if ($isTextNode(node)) {
                  const currentStyle = node.getStyle() || '';
                  node.setStyle(`color: ${color}; ${currentStyle}`);
                }
              };
              
              root.getAllTextNodes().forEach(applyColor);
            }
            
            // Note: Underline and highlight are usually embedded in the HTML itself
            // So we don't need to force apply them if they're not in the HTML
            // The contentHighlight, contentUnderline flags are mainly for UI state
          }
          
          initializedRef.current = true;
        });
      }, 100); // Delayed to ensure content is fully loaded first
    }
  }, [color, underline, highlight, editor]);

  return null;
}

const LexicalTextEditor = ({
  content,
  onContentChange,
  fontSize = 14,
  fontFamily = "Roboto",
  placeholder = "",
  style = {},
  onKeyDown,
  id,
  onColorChange,
  onUnderlineChange,
  onHighlightChange,
  onNumberedList,
  onBulletedList,
  onLinkChange,
  onBlur,
  saveToHistory,
  // New props for highlighting state
  color,
  underline,
  highlight,
}) => {
  const initialConfig = {
    namespace: `TextboxEditor-${id}`,
    html: { import: buildImportMap() }, // CRITICAL: Patches HTML importers for proper list/text handling
    theme: {
      text: {
        bold: "lexical-text-bold",
        italic: "lexical-text-italic",
        underline: "lexical-text-underline",
        underlineStrikethrough:
          "lexical-text-underline lexical-text-strikethrough",
        strikethrough: "lexical-text-strikethrough",
        code: "rounded bg-gray-100 px-1.5 py-0.5 font-mono text-sm",
      },
      list: {
        nested: {
          listitem: "list-item-nested",
        },
        ol: "list-decimal list-inside",
        ul: "list-disc list-inside",
        listitem: "list-item",
      },
      link: "text-blue-600 underline hover:text-blue-800",
      // Add support for custom styling
      paragraph: "mb-1",
      quote: "border-l-4 border-gray-300 pl-4 italic",
      heading: {
        h1: "text-2xl font-bold",
        h2: "text-xl font-bold",
        h3: "text-lg font-bold",
        h4: "text-base font-bold",
        h5: "text-sm font-bold",
      },
    },
    onError: (error) => {},
    nodes: [
      HeadingNode,
      ListNode,
      ListItemNode,
      QuoteNode,
      LinkNode,
      MarkNode, // <-- Use Lexical's built-in node for inline styles
    ],
    // Enable HTML parsing and serialization
    editable: true,
    // Add support for custom styling
    editorState: null,
  };
  return (
    <>
      <style>{`
        .lexical-text-bold {
          font-weight: bold;
        }
        .lexical-text-italic {
          font-style: italic;
        }
        .lexical-text-underline {
          text-decoration: underline;
        }
        .lexical-text-strikethrough {
          text-decoration: line-through;
        }
        .lexical-text-underline.lexical-text-strikethrough {
          text-decoration: underline line-through;
        }
        /* Make light-colored text visible on white backgrounds */
        [data-lexical-editor="${id}"] * {
          /* Apply text shadow to elements with light colors */
        }
        [data-lexical-editor="${id}"] *[style*="color"] {
          /* Additional styles applied via LightTextVisibilityPlugin */
        }
      `}</style>
      <LexicalComposer initialConfig={initialConfig}>
        <div style={{ position: "relative", width: "100%" }}>
          <RichTextPlugin
            contentEditable={
              <ContentEditable
                id={id}
                data-lexical-editor={id}
                style={{
                  width: "100%",
                  height: "120px",
                  padding: "12px",
                  fontSize: `${fontSize}px`,
                  fontFamily: fontFamily,
                  border: "1px solid #ddd",
                  borderRadius: "4px",
                  overflowY: "auto",
                  outline: "none",
                  backgroundColor: "white",
                  lineHeight: "1.4",
                  boxSizing: "border-box",
                  resize: "none",
                  ...style,
                }}
                placeholder={placeholder}
                onKeyDown={onKeyDown}
                onBlur={(e) => {
                  if (onBlur) {
                    onBlur(e);
                  }
                }}
              />
            }
            placeholder={null}
            ErrorBoundary={LexicalErrorBoundary}
          />
          <ContentInitializationPlugin content={content} id={id} color={color} underline={underline} highlight={highlight} />
          <HighlightingStatePreservationPlugin color={color} underline={underline} highlight={highlight} />
          <PastePlugin />
          <FormattingPlugin
            onColorChange={onColorChange}
            onUnderlineChange={onUnderlineChange}
            onHighlightChange={onHighlightChange}
            onNumberedList={onNumberedList}
            onBulletedList={onBulletedList}
            onLinkChange={onLinkChange}
          />
          <HTMLExportOnChangePlugin onContentChange={onContentChange} />
          <BlurPlugin onBlur={onBlur} />
          <LightTextVisibilityPlugin editorId={id} />
          <HistoryPlugin />
          <AutoFocusPlugin />
          <ListPlugin />
          <LinkPlugin />
          <SingleLevelListPlugin />
          <ColorPlugin />
        </div>
      </LexicalComposer>
    </>
  );
};

export default LexicalTextEditor;
